import { useQueryClient } from '@tanstack/react-query';
import { useContext, useMemo, useState } from 'react';
import { CancelButton } from 'src/components/Buttons';
import { OkButton } from 'src/components/Buttons/OkButton';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext/ActivePosEntityContextV2';
import { Content } from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { Stack } from 'src/core/ui';
import { useRefundGroupPayments } from 'src/hooks/api/useAdyenPaymentSession';
import type { ModalProps } from 'src/modals/Modal';
import {
  ModalBody,
  ModalBodyDataContainer,
  ModalFooter,
} from 'src/modals/Modal/Modal.styled';
import { ModalHeader } from 'src/modals/Modal/ModalHeader';
import { ContentId } from 'src/utils/constants/contentId';
import { formatCurrency } from 'src/utils/numberFormatter';
import {
  MarketplacePaymentLineType,
  MarketplacePaymentState,
  SaleDetails,
} from 'src/WebApiController';

import * as styles from './MarketplaceRefundDialog.css';

type ModalConfig = Omit<ModalProps, 'fullscreen'>;

type MarketplaceRefundDialogProps = {
  sale: SaleDetails;
  cancelTo: ModalConfig | null;
};

type RefundGroup = {
  extPmtId: string;
  pmtRefId: string | null;
  lines: {
    id: number;
    type: MarketplacePaymentLineType;
    amount: number;
    currency: string | null;
    state: MarketplacePaymentState;
  }[];
};

export const MarketplaceRefundDialog = ({
  sale,
  cancelTo,
}: MarketplaceRefundDialogProps) => {
  const queryClient = useQueryClient();
  const { setModal, closeModal } = useContext(ModalContext);
  const { setActivePosEntity } = useActivePosEntityContext<SaleDetails>();
  const refundGroupPayments = useRefundGroupPayments();

  const [isRefunding, setIsRefunding] = useState(false);

  const handleClose = () => {
    if (cancelTo) {
      setModal(cancelTo);
    } else {
      closeModal(true);
    }
  };

  // Group paid/partially-paid/pending-reversal mkpLines by pmtRefId (PSP reference) when available,
  // falling back to extPmtId (merchant reference) for backward compatibility.
  const refundGroups = useMemo<RefundGroup[]>(() => {
    const visibleLines = (sale.mkpLines ?? []).filter(
      (l) =>
        l.state === MarketplacePaymentState.Paid ||
        l.state === MarketplacePaymentState.PartiallyPaid ||
        l.state === MarketplacePaymentState.PendingReversal
    );

    const groupMap = new Map<string, RefundGroup>();
    for (const line of visibleLines) {
      const pmtRefId = line.pmtRefId ?? null;
      const key = pmtRefId ?? line.extPmtId;
      if (!groupMap.has(key)) {
        groupMap.set(key, { extPmtId: line.extPmtId, pmtRefId, lines: [] });
      }
      groupMap.get(key)!.lines.push({
        id: line.id,
        type: line.type,
        amount: line.amt.amt,
        currency: line.amt.currency,
        state: line.state,
      });
    }
    return Array.from(groupMap.values());
  }, [sale.mkpLines]);

  // All refundable groups are those that have at least one non-PendingReversal line
  const allGroupsPendingReversal = refundGroups.every((g) =>
    g.lines.every((l) => l.state === MarketplacePaymentState.PendingReversal)
  );

  const handleRefundAll = async () => {
    if (!sale.salesGroupId) {
      return;
    }
    setIsRefunding(true);
    try {
      await refundGroupPayments.mutateAsync(sale.salesGroupId);
      await queryClient.invalidateQueries({
        queryKey: ['getRetailSaleDetails', sale.id],
      });
      await setActivePosEntity(sale.id, sale.idOnMkp, true);
      handleClose();
    } finally {
      setIsRefunding(false);
    }
  };

  return (
    <>
      <ModalHeader onClose={handleClose}>
        <div className={styles.headerText}>
          <Content id={ContentId.Refund} />
        </div>
      </ModalHeader>
      <ModalBody>
        <ModalBodyDataContainer>
          <div className={styles.groupsContainer}>
            {refundGroups.map((group) => {
              const groupKey = group.pmtRefId ?? group.extPmtId;
              return (
                <div key={groupKey} className={styles.groupContainer}>
                  <div className={styles.groupHeader}>
                    <span className={styles.merchantRef}>{group.extPmtId}</span>
                  </div>
                  <Stack direction="column" gap="s">
                    {group.lines.map((line) => (
                      <div key={line.id} className={styles.lineRow}>
                        <span className={styles.lineType}>{line.type}</span>
                        <span>
                          {formatCurrency(line.amount, line.currency)}
                        </span>
                      </div>
                    ))}
                  </Stack>
                </div>
              );
            })}
          </div>
        </ModalBodyDataContainer>
      </ModalBody>
      <ModalFooter>
        <Stack justifyContent="end" gap="m">
          <CancelButton onClick={handleClose} disabled={isRefunding} />
          <OkButton
            textContentId={ContentId.Refund}
            variant="outlineBrand"
            textColor="error"
            size="md"
            disabled={isRefunding || allGroupsPendingReversal}
            onClick={handleRefundAll}
          />
        </Stack>
      </ModalFooter>
    </>
  );
};
